import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import {
  CreditCard,
  Lock,
  ShieldCheck,
  ArrowRight,
  ChevronLeft,
  Calendar as CalendarIcon,
  MapPin,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


function PaymentPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [card, setCard] = useState({ number: "", expiry: "", cvv: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!state) {
      toast.error("Missing booking details. Please restart the process.");
      navigate("/");
    }
  }, [state, navigate]);

  const handleChange = (e) => {
    setCard({ ...card, [e.target.name]: e.target.value });
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    const packageId = state?.accommodation?._id;
    if (!packageId) {
      toast.error("Invalid accommodation selection");
      navigate("/");
      return;
    }

    const bookingPayload = {
      userName: state.userName || "Guest",
      email: state.email || "no-email@example.com",
      phone: state.phone || "N/A",
      accommodation: packageId,
      qty: state.qty || 1,
      totalPrice: state.totalPrice || 0,
    };

    try {
      const res = await fetch(`${API_URL}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingPayload),
      });

      if (res.ok) {
        toast.success("Payment Successful! Journey confirmed.");
        navigate("/booking-success", { state: bookingPayload });
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(`Payment failed: ${errData.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!state) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-sky-600 font-bold text-sm mb-8 transition-colors group"
          >
            <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            Back to Booking
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Left: Payment Form (3 cols) */}
            <div className="lg:col-span-3 space-y-8">
              <div className="bg-white rounded-[2.5rem] shadow-xl shadow-sky-900/5 p-10 border border-sky-100/50">
                <div className="mb-10 flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-black text-sky-950 tracking-tight">Checkout</h1>
                    <p className="text-gray-500 font-medium mt-2">Finalize your payment securely</p>
                  </div>
                  <div className="p-4 bg-sky-50 rounded-2xl text-sky-600">
                    <ShieldCheck size={32} />
                  </div>
                </div>

                <form onSubmit={handlePay} className="space-y-8">
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                        <CreditCard size={12} /> Card Number
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="number"
                          value={card.number}
                          onChange={handleChange}
                          className="w-full px-6 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950 tracking-widest"
                          placeholder="1111 2222 3333 4444"
                          required
                          maxLength="19"
                        />
                        <div className="absolute right-5 top-1/2 -translate-y-1/2 flex gap-2">
                          <div className="w-8 h-5 bg-sky-900/10 rounded flex items-center justify-center text-[8px] font-black italic">VISA</div>
                          <div className="w-8 h-5 bg-sky-900/10 rounded flex items-center justify-center text-[8px] font-black italic">MC</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <label className="text-[10px] font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                          <CalendarIcon size={12} /> Expiry Date
                        </label>
                        <input
                          type="text"
                          name="expiry"
                          value={card.expiry}
                          onChange={handleChange}
                          className="w-full px-6 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950"
                          placeholder="MM / YY"
                          required
                          maxLength="5"
                        />
                      </div>
                      <div className="space-y-3">
                        <label className="text-[10px] font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                          <Lock size={12} /> Security CVV
                        </label>
                        <input
                          type="password"
                          name="cvv"
                          value={card.cvv}
                          onChange={handleChange}
                          className="w-full px-6 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950"
                          placeholder="•••"
                          required
                          maxLength="4"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Tips */}
                  <div className="bg-sky-50/50 rounded-3xl p-6 border border-sky-100/50">
                    <div className="flex items-center gap-2 text-sky-900 text-xs font-black uppercase tracking-widest mb-3">
                      <CheckCircle2 size={16} className="text-emerald-500" />
                      Secure Test Credentials
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <p className="text-[9px] font-black text-sky-900/30 uppercase tracking-tight leading-none">Card Number</p>
                        <p className="text-xs font-black text-sky-900">1111 2222 3333 4444</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[9px] font-black text-sky-900/30 uppercase tracking-tight leading-none">Expiry</p>
                        <p className="text-xs font-black text-sky-900">12/26</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[9px] font-black text-sky-900/30 uppercase tracking-tight leading-none">CVV</p>
                        <p className="text-xs font-black text-sky-900">123</p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isProcessing}
                    className={`w-full py-5 rounded-2xl font-black text-lg transition-all flex items-center justify-center gap-3 group shadow-xl 
                      ${isProcessing ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-sky-950 text-white shadow-sky-950/20 hover:bg-black hover:scale-[1.01]'}`}
                  >
                    {isProcessing ? (
                      <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        Pay LKR {state.totalPrice?.toLocaleString()}
                        <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>

                  <div className="flex items-center justify-center gap-2 pt-4 opacity-30 group grayscale hover:grayscale-0 transition-all duration-700">
                    <div className="text-[10px] font-black uppercase tracking-[0.3em] text-sky-950 flex items-center gap-2">
                      <Lock size={12} /> Encrypted Gateway
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Right: Order Summary (2 cols) */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white rounded-[2.5rem] shadow-xl shadow-sky-900/5 border border-sky-100/50 overflow-hidden sticky top-24">
                <div className="p-8 border-b border-gray-50">
                  <h3 className="text-xl font-black text-sky-950 tracking-tight">Order Summary</h3>
                  <p className="text-gray-400 text-xs font-bold mt-1">Review your journey details</p>
                </div>

                <div className="p-8 space-y-8">
                  {/* Reservation Detail */}
                  <div className="flex items-start gap-4">
                    <img
                      src={state.accommodation?.images?.[0] || "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?q=80&w=1000"}
                      className="w-20 h-20 rounded-2xl object-cover shadow-lg shadow-sky-900/10"
                      alt="Acc"
                    />
                    <div className="flex-1">
                      <h4 className="text-sky-950 font-black tracking-tight leading-tight mb-1">{state.accommodation?.accommodationName}</h4>
                      <p className="text-sky-500 text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                        <MapPin size={10} /> Sri Lanka
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 pt-8 border-t border-gray-50">
                    <div className="flex justify-between items-center bg-gray-50 p-4 rounded-2xl border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-sky-200/50 rounded-xl flex items-center justify-center text-sky-600">
                          <CheckCircle2 size={16} />
                        </div>
                        <span className="text-xs font-black text-sky-900 uppercase tracking-tight">Booking For</span>
                      </div>
                      <span className="text-sm font-bold text-sky-700">{state.qty} Guests</span>
                    </div>

                    <div className="flex justify-between items-center bg-gray-50 p-4 rounded-2xl border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-sky-200/50 rounded-xl flex items-center justify-center text-sky-600">
                          <AlertCircle size={16} />
                        </div>
                        <span className="text-xs font-black text-sky-900 uppercase tracking-tight">Base Price</span>
                      </div>
                      <span className="text-sm font-bold text-sky-700">LKR {state.accommodation?.pricePerNight?.toLocaleString()} / night</span>
                    </div>
                  </div>

                  <div className="pt-8 border-t border-sky-950/10 mb-2">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-[11px] font-black text-sky-900/30 uppercase tracking-widest mb-1">Total Amount</p>
                        <h2 className="text-4xl font-black text-sky-950 leading-none tracking-tighter">
                          LKR {state.totalPrice?.toLocaleString()}
                        </h2>
                      </div>
                      <div className="text-right pb-1">
                        <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Included Tax</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>

      <Fotter />
    </div>
  );
}

export default PaymentPage;
